import React, { useState, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { toJS } from 'mobx'
import CurrentLocationIcon from '../../../../../../assets/images/icons/current-location-icon.svg'
import HistoryIcon from '../../../../../../assets/images/icons/history-icon.svg'

const MAX_RECENT_SEARCHES_DISPLAY = 3
const SPECIAL_AREA = 'South of Highway'

const MapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M10.8687 2.53201L5.95038 0.0534919C5.81565 -0.0144038 5.65746 -0.0178332 5.51992 0.0441595L0.294551 2.39925C0.115269 2.48006 0 2.65844 0 2.85509V15.1724C0 15.5353 0.374549 15.7773 0.705449 15.6282L5.51992 13.4583C5.65746 13.3963 5.81565 13.3997 5.95038 13.4676L10.6437 15.8328C10.7852 15.9041 10.9522 15.9041 11.0937 15.8328L15.725 13.4989C15.8936 13.4139 16 13.2412 16 13.0524V0.75802C16 0.385641 15.6075 0.143932 15.275 0.311511L10.8687 2.53201ZM10.3206 3.59955L6.35649 1.60191V12.3356C6.40178 12.3537 6.44646 12.3739 6.49041 12.396L10.3206 14.3262V3.59955ZM11.5206 3.54727V14.2739L14.8 12.6213V1.89464L11.5206 3.54727ZM1.2 3.30741L5.15649 1.52421V12.3121C5.11277 12.3277 5.06951 12.3451 5.02684 12.3643L1.2 14.0891V3.30741Z" fill="currentColor"/>
  </svg>
)

const getOptionValue = (option) => option.value || option.id

const AreasList = ({
  options = [],
  optionsById = {},
  selectedOptionsGroup = [],
  onApplyResults,
  countResults = 0,
  onChangeTown,
  className = '',
  recentSearches = [],
  onSelectRecentSearch,
  onSelectCurrentLocation,
  openMapView,
  hideRecentSearches = false,
  hideActions = false
}) => {
  const [selectedValues, setSelectedValues] = useState(() =>
    selectedOptionsGroup.map(option => option.value || option.id)
  )

  useEffect(() => {
    setSelectedValues(selectedOptionsGroup.map(option => option.value || option.id))
  }, [selectedOptionsGroup])

  const getChildren = useCallback((option, excludeSpecialArea = false) => {
    const children = (option.children || [])
      .map(childId => optionsById[childId])
      .filter(Boolean)

    return excludeSpecialArea
      ? children.filter(child => getOptionValue(child) !== SPECIAL_AREA)
      : children
  }, [optionsById])

  const getAllDescendantIds = useCallback(function recurse(option, accumulator = []) {
    const children = getChildren(option, true)
    children.forEach(child => {
      accumulator.push(getOptionValue(child))
      recurse(child, accumulator)
    })
    return accumulator
  }, [getChildren])

  const toggleOption = useCallback((option) => {
    const plainOption = toJS(option)
    const optionValue = getOptionValue(plainOption)
    const descendantIds = getAllDescendantIds(option)
    const isCurrentlySelected = selectedValues.includes(optionValue)

    const newValues = isCurrentlySelected
      ? selectedValues.filter(value => value !== optionValue && !descendantIds.includes(value))
      : [...new Set([...selectedValues, optionValue, ...descendantIds])]

    setSelectedValues(newValues)
    onChangeTown?.({ value: newValues })
  }, [selectedValues, getAllDescendantIds, onChangeTown])

  const getAllChildIds = useCallback((options) => {
    const childIds = new Set()
    options.forEach(option => {
      getChildren(option).forEach(child => {
        childIds.add(getOptionValue(child))
        getChildren(child).forEach(nested => childIds.add(getOptionValue(nested)))
      })
    })
    return childIds
  }, [getChildren])

  const topLevelOptions = useMemo(() => {
    const allOptions = toJS(options)
    const childIds = getAllChildIds(allOptions)
    return allOptions.filter(option => !childIds.has(getOptionValue(option)))
  }, [options, getAllChildIds])

  const renderOption = useCallback((option, level = 0) => {
    const plainOption = toJS(option)
    const optionValue = getOptionValue(plainOption)
    const optionName = plainOption.name || plainOption.label || plainOption.title || 'Unknown Area'
    const children = getChildren(option)
    const isTheHamptons = level === 0 && optionName === 'The Hamptons'

    return (
      <div key={optionValue} className={`AreasList-option AreasList-option--level-${level}`}>
        <div className='AreasList-optionRow'>
          <label className='AreasList-optionContent'>
            <input
              type='checkbox'
              checked={selectedValues.includes(optionValue)}
              onChange={() => toggleOption(option)}
              className='AreasList-checkbox'
            />
            <span className='AreasList-optionText'>
              {optionName}
              {isTheHamptons && <span className='AreasList-selectAll'>&nbsp;(Select All)</span>}
              {plainOption.count != null && <span className='AreasList-count'>({plainOption.count})</span>}
            </span>
          </label>
          {isTheHamptons && (
            <button type='button' className='AreasList-showMapButton' onClick={openMapView}>
              <MapIcon />
              <span>View Map</span>
            </button>
          )}
        </div>
        {children.length > 0 && (
          <div className='AreasList-children'>
            {children.map(child => renderOption(child, level + 1))}
          </div>
        )}
      </div>
    )
  }, [selectedValues, toggleOption, getChildren, openMapView])

  return (
    <div className={`AreasList ${className}`}>
      <div className='AreasList-scrollableContent'>
        {!hideRecentSearches && recentSearches.length > 0 && (
          <div className='AreasList-searchHistory'>
            <div className='AreasList-currentLocation' onClick={onSelectCurrentLocation}>
              <div className='AreasList-currentLocationIcon'>
                <img src={CurrentLocationIcon} alt='' />
              </div>
              <div className='AreasList-currentLocationText'>Current Location</div>
            </div>

            <div className='AreasList-searchHistoryTitle'>SEARCH HISTORY</div>
            <div className='AreasList-searchHistoryItems'>
              {recentSearches.slice(0, MAX_RECENT_SEARCHES_DISPLAY).map((search) => (
                <div
                  key={search.id}
                  className='AreasList-searchHistoryItem'
                  onClick={() => onSelectRecentSearch?.(search)}
                >
                  <div className='AreasList-searchHistoryIcon'>
                    <img src={HistoryIcon} alt='' />
                  </div>
                  <div className='AreasList-searchHistoryText'>{search.title}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className='AreasList-options'>
          {topLevelOptions.map(option => renderOption(option))}
        </div>
      </div>

      {!hideActions && (
        <div className='AreasList-actions'>
          <button type='button' className='AreasList-clearButton'>
            Clear
          </button>
          <button type='button' className='AreasList-submitButton Button Button--primary' onClick={onApplyResults}>
            Show Homes {countResults !== null && countResults !== undefined && `(${countResults})`}
          </button>
        </div>
      )}
    </div>
  )
}

AreasList.propTypes = {
  className: PropTypes.string,
  countResults: PropTypes.number,
  hideActions: PropTypes.bool,
  hideRecentSearches: PropTypes.bool,
  onApplyResults: PropTypes.func,
  onChangeTown: PropTypes.func,
  onSelectCurrentLocation: PropTypes.func,
  onSelectRecentSearch: PropTypes.func,
  openMapView: PropTypes.func.isRequired,
  options: PropTypes.array,
  optionsById: PropTypes.object,
  recentSearches: PropTypes.array,
  selectedOptionsGroup: PropTypes.array
}

export default AreasList